import { Event } from '@bugsnag/js';
import { isAlreadyReported } from './recently-reported';

/**
 * An absolute URL or a `/path` followed by a query or fragment, inside free text. The query ends at
 * whitespace, a quote, or a trailing ":" (so "Http failure response for <url>: 500" keeps its colon).
 */
const QUERY_IN_TEXT = /(https?:\/\/[^\s?#"']+|(?<![\w:/.])\/[^\s?#"']*)[?#][^\s"']*?(?=:?(?:\s|["']|$))/gi;
const EXTENSION_FRAME = /^(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i;
const NOISE_MESSAGES = [
  /ResizeObserver loop/i,
  // Firefox's AbortError for promises still pending while the page unloads.
  /browsing context is going away/i,
];
/** Breadcrumb metadata keys that hold a single URL or path: navigation `from`/`to`, request `url`. */
const URL_KEYS = new Set(['from', 'to', 'url']);

/** App-specific path cleanup, e.g. spotlight-ui strips its leading location segment. */
export type PathNormalizer = (path: string) => string;
const keepPath: PathNormalizer = path => path;

/** Removes query strings and fragments from every URL or `/path` in free text, such as an error message. */
export function stripQueryStrings(text: string): string {
  return text.replaceAll(QUERY_IN_TEXT, '$1');
}

/**
 * For a field that holds one URL or path: drops the query string and applies `normalizePath`
 * to the path. Values that aren't a URL or path only lose their query string.
 */
export function scrubUrl(value: string, normalizePath: PathNormalizer = keepPath): string {
  const withoutQuery = value.split(/[?#]/)[0];
  if (withoutQuery.startsWith('/')) {
    return normalizePath(withoutQuery);
  }
  try {
    const url = new URL(withoutQuery);
    return url.origin + normalizePath(url.pathname);
  } catch {
    return withoutQuery;
  }
}

function isNoise(event: Event): boolean {
  // An HTTP failure ErrorTrackingInterceptor already reported, reaching Bugsnag again uncaught.
  if (isAlreadyReported(event.originalError)) {
    return true;
  }
  const error = event.errors[0];
  if (!error) {
    return false;
  }
  if (NOISE_MESSAGES.some(pattern => pattern.test(error.errorMessage ?? ''))) {
    return true;
  }
  const topFrame = error.stacktrace?.[0];
  return !!topFrame?.file && EXTENSION_FRAME.test(topFrame.file);
}

/**
 * Bugsnag `onError` callback (wired by `startBugsnag`): drops known noise, removes query strings,
 * and applies the app's `normalizePath` to URL fields before an event is sent. Returning false
 * discards it. Runs before any `onError` passed to `notify`, so values set there aren't scrubbed here.
 */
export function scrubBugsnagEvent(event: Event, normalizePath: PathNormalizer = keepPath): boolean {
  if (isNoise(event)) {
    return false;
  }

  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url, normalizePath);
  }
  if (event.context) {
    event.context = scrubUrl(event.context, normalizePath);
  }
  event.errors.forEach(error => {
    error.errorMessage = stripQueryStrings(error.errorMessage ?? '');
  });
  event.breadcrumbs.forEach(breadcrumb => {
    Object.entries(breadcrumb.metadata ?? {}).forEach(([key, value]) => {
      if (typeof value === 'string') {
        breadcrumb.metadata[key] = URL_KEYS.has(key) ? scrubUrl(value, normalizePath) : stripQueryStrings(value);
      }
    });
  });

  return true;
}
